import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * useDaySession — global day-session state for the whole app.
 *
 * Owner directive (Phase 1.6): cashiers cannot ring orders while no
 * day session is open. Single source of truth for "is the day open?"
 * across every component (POS canvas, top bar, KDS, etc.).
 *
 * Cohesion rule 3: state changes emit ONE event. Refresh-this on
 * window focus + after any open/close action so all components see
 * the latest state automatically.
 */

export interface DaySessionInfo {
  id: string;
  branchId: string;
  openedAt: string;
  openingFloatCents: number;
  openedBy: { id: string; name: string; role: string };
}

export interface ClosedSessionInfo extends DaySessionInfo {
  closedAt: string;
  countedCashCents: number;
  expectedCashCents: number;
  varianceCents: number;
  closingFloatCents: number;
  closeNotes: string | null;
}

export interface ClosedTodayInfo {
  id: string;
  openedAt: string;
  closedAt: string;
  openingFloatCents: number;
  closingFloatCents: number | null;
  countedCashCents: number | null;
  expectedCashCents: number | null;
  varianceCents: number | null;
  openedBy: { id: string; name: string } | null;
  closedBy: { id: string; name: string } | null;
}

export interface UnreviewedAutoClose {
  id: string;
  openedAt: string;
  autoClosedAt: string | null;
  autoCloseReason: string | null;
  openingFloatCents: number;
  expectedCashCents: number | null;
  openedBy: { name: string } | null;
}

export class OpenDayBlockedError extends Error {
  code: string;
  autoClosed?: UnreviewedAutoClose;
  constructor(message: string, code: string, autoClosed?: UnreviewedAutoClose) {
    super(message);
    this.name = 'OpenDayBlockedError';
    this.code = code;
    this.autoClosed = autoClosed;
  }
}

interface ContextValue {
  /** The currently open day session, or null if the day isn't open. */
  current: DaySessionInfo | null;
  /** Closed-today session — present when the day was already closed (Phase 4.6). Locks the till for the rest of the calendar day. */
  closedToday: ClosedTodayInfo | null;
  loading: boolean;
  /** Refetch from the server. Call after sign-in / focus / open / close. */
  refresh: () => Promise<void>;
  /** Open the day with the supplied opening cash float. Resolves on success.
   *  Throws OpenDayBlockedError when the server demands an auto-close review first. */
  openDay: (openingFloatCents: number) => Promise<DaySessionInfo>;
  /** Submit the auto-close review for a previously auto-closed session. */
  reviewAutoClosed: (sessionId: string, input: { countedCashCents: number; reviewNotes: string }) => Promise<void>;
  /** Reopen a same-day closed session for late sales. Requires manager PIN. */
  reopenDay: (sessionId: string, reopenReason: string) => Promise<DaySessionInfo>;
  /** Close the day. Returns the closed session. */
  closeDay: (input: {
    countedCashCents: number;
    closingFloatCents: number;
    closeNotes?: string;
  }) => Promise<ClosedSessionInfo>;
}

const DaySessionContext = createContext<ContextValue | null>(null);

const URL_CURRENT = '/api/day-sessions/current';
const URL_OPEN    = '/api/day-sessions/open';
const URL_CLOSE   = '/api/day-sessions/close';

interface CurrentResponse {
  session: DaySessionInfo | null;
  closedToday: ClosedTodayInfo | null;
}

async function fetchCurrent(): Promise<CurrentResponse> {
  try {
    const r = await fetch(URL_CURRENT, { credentials: 'include' });
    if (!r.ok) return { session: null, closedToday: null };
    const body = (await r.json()) as CurrentResponse;
    return { session: body.session ?? null, closedToday: body.closedToday ?? null };
  } catch {
    return { session: null, closedToday: null };
  }
}

export function DaySessionProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<DaySessionInfo | null>(null);
  const [closedToday, setClosedToday] = useState<ClosedTodayInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const r = await fetchCurrent();
    setCurrent(r.session);
    setClosedToday(r.closedToday);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refetch when tab regains focus (a supervisor might open the day
  // from a different device).
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const openDay = useCallback(
    async (openingFloatCents: number): Promise<DaySessionInfo> => {
      const r = await fetch(URL_OPEN, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ openingFloatCents }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (body.code === 'day.unreviewed_autoclose') {
          throw new OpenDayBlockedError(body.error ?? 'Auto-close review required', body.code, body.autoClosed);
        }
        throw new Error(body.error ?? 'Could not open day');
      }
      const body = (await r.json()) as { session: DaySessionInfo };
      setCurrent(body.session);
      setClosedToday(null);
      return body.session;
    },
    [],
  );

  const reviewAutoClosed = useCallback(
    async (sessionId: string, input: { countedCashCents: number; reviewNotes: string }) => {
      const r = await fetch(`/api/day-sessions/${sessionId}/review`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? 'Could not submit review');
      }
    },
    [],
  );

  const reopenDay = useCallback(
    async (sessionId: string, reopenReason: string): Promise<DaySessionInfo> => {
      const r = await fetch(`/api/day-sessions/${sessionId}/reopen`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reopenReason }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? 'Could not reopen day');
      }
      const body = (await r.json()) as { session: DaySessionInfo };
      setCurrent(body.session);
      setClosedToday(null);
      return body.session;
    },
    [],
  );

  const closeDay = useCallback(
    async (input: { countedCashCents: number; closingFloatCents: number; closeNotes?: string }) => {
      const r = await fetch(URL_CLOSE, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const err = new Error(body.error ?? 'Could not close day') as Error & {
          varianceCents?: number;
          thresholdCents?: number;
        };
        err.varianceCents = body.varianceCents;
        err.thresholdCents = body.thresholdCents;
        throw err;
      }
      const body = (await r.json()) as { session: ClosedSessionInfo };
      setCurrent(null);
      // After close, the next /current fetch will populate closedToday.
      // Refetch immediately so the post-close screen flips on right away.
      void refresh();
      return body.session;
    },
    [refresh],
  );

  return (
    <DaySessionContext.Provider value={{ current, closedToday, loading, refresh, openDay, reviewAutoClosed, reopenDay, closeDay }}>
      {children}
    </DaySessionContext.Provider>
  );
}

export function useDaySession() {
  const ctx = useContext(DaySessionContext);
  if (!ctx) throw new Error('useDaySession must be inside <DaySessionProvider>');
  return ctx;
}
